import { supabaseService } from '../supabaseService';

let currentSessionId: string | null = null;
const trackedImpressionsInSession = new Set<string>();

/**
 * Get or create a persistent session ID for the browser tab/session
 */
export function getOrCreateSessionId(): string {
  if (currentSessionId) return currentSessionId;
  try {
    let sid = sessionStorage.getItem('lit_ad_session_id');
    if (!sid) {
      sid = 'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);
      sessionStorage.setItem('lit_ad_session_id', sid);
    }
    currentSessionId = sid;
    return sid;
  } catch {
    return 'sess_' + Date.now();
  }
}

/**
 * Record an ad event (impression, view, click, session) associated with the user
 */
export async function trackAdActivity(params: {
  eventType: 'impression' | 'view' | 'session' | 'click';
  placement?: string;
  uniqueKey?: string;
}): Promise<void> {
  try {
    const { eventType, placement, uniqueKey } = params;
    const sessionId = getOrCreateSessionId();

    // Prevent excessive duplicate impressions for the exact same ad unit within a few seconds
    if (uniqueKey) {
      const dedupKey = `${uniqueKey}_${eventType}`;
      if (trackedImpressionsInSession.has(dedupKey)) {
        return;
      }
      trackedImpressionsInSession.add(dedupKey);
      // Clean up after 30 seconds
      setTimeout(() => trackedImpressionsInSession.delete(dedupKey), 30000);
    }

    // Determine current user ID
    let userId: string | null = null;
    const currentUser = await supabaseService.getCurrentUser().catch(() => null);
    if (currentUser?.id) {
      userId = currentUser.id;
    } else {
      // Fallback: check localStorage for cached user
      const stored = localStorage.getItem('supabase.auth.token');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          userId = parsed?.currentSession?.user?.id || parsed?.user?.id || null;
        } catch {
          // ignore
        }
      }
    }

    if (!userId) {
      // Still send session id if available
      return;
    }

    const payload = {
      userId,
      eventType,
      sessionId,
      placement: placement || 'unspecified',
      date: new Date().toISOString().split('T')[0],
      path: window.location.pathname,
    };

    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon('/api/ad-activity', blob);
    } else {
      fetch('/api/ad-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (err) {
    // Non-blocking
  }
}
